import { inject } from '@angular/core';
import {
  AbstractControlOptions,
  AsyncValidatorFn,
  FormControl,
  FormControlState,
  NonNullableFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Suggestion } from '@elio/emanager-front/shared/models';
import { LocalDate, LocalTime } from '@elio/shared/models';

export const emptyFormControl = <T>(
  formState: T | FormControlState<T> | undefined,
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<T | undefined> => inject(NonNullableFormBuilder).control(formState, validatorOrOpts, asyncValidator);

export const customControl = <T>(
  formState: T | FormControlState<T>,
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<T> => inject(NonNullableFormBuilder).control(formState, validatorOrOpts, asyncValidator);

export const idControl = (): FormControl<number> => customControl<number>(-1, Validators.min(1));

export const emptyString = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<string | undefined> => emptyFormControl<string>(undefined, validatorOrOpts, asyncValidator);

export const emptyNumber = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<number | undefined> => emptyFormControl<number>(undefined, validatorOrOpts, asyncValidator);

export type BooleanOrNull = boolean | null | undefined;

export const booleanOrNull = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<BooleanOrNull> => emptyFormControl<BooleanOrNull>(undefined, validatorOrOpts, asyncValidator);

export const emptyLocalDate = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<LocalDate | undefined> => emptyFormControl<LocalDate>(undefined, validatorOrOpts, asyncValidator);

export const emptyLocalTime = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<LocalTime | undefined> => emptyFormControl<LocalTime>(undefined, validatorOrOpts, asyncValidator);

export const emptyDate = (
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<Date | undefined> => emptyFormControl<Date>(undefined, validatorOrOpts, asyncValidator);

export const emptyArray = <T>(
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<T[]> => customControl<T[]>([], validatorOrOpts, asyncValidator);

export const emptySuggestion = <T>(
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
): FormControl<Suggestion<T> | undefined> =>
  emptyFormControl<Suggestion<T>>(undefined, validatorOrOpts, asyncValidator);
